import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const DayInLifeSection = () => {
  const [activeTab, setActiveTab] = useState('meeting');

  const activities = {
    meeting: {
      title: "Monthly General Meeting",
      time: "10:00 AM - 12:00 PM",
      location: "Leo Youth Center",
      locationLink: "https://maps.app.goo.gl/ZqCotTGzu681EnFa6",
      description: "Professional meeting environment with structured agenda and meaningful discussions",
      schedule: [
        { time: "10:00 AM", activity: "Meeting call to order and opening ceremony", icon: "Bell" },
        { time: "10:30 AM", activity: "Committee reports and project updates", icon: "FileText" },
        { time: "11:15 AM", activity: "New business and project planning", icon: "Lightbulb" },
        { time: "11:45 AM", activity: "Closing remarks and next steps", icon: "CheckCircle" }
      ],
      image: "assets/images/mis/meeting.jpg"
    },
    project: {
      title: "Community Service Project",
      time: "9:00 AM - 4:00 PM",
      location: "Various Community Locations",
      description: "Hands-on service activities making direct impact in underserved communities",
      schedule: [
        { time: "9:00 AM", activity: "Team assembly and project briefing", icon: "Users" },
        { time: "10:00 AM", activity: "Active service implementation", icon: "Heart" },
        { time: "12:30 PM", activity: "Lunch break and team bonding", icon: "Coffee" },
        { time: "3:30 PM", activity: "Wrap-up and impact documentation", icon: "Camera" }
      ],
      image: "assets/images/mis/service_project2.jpg"
    },
    training: {
      title: "Leadership Development Workshop",
      time: "2:00 PM - 6:00 PM",
      location: "Various Places",
      description: "Skill-building sessions focused on leadership, communication, and professional development",
      schedule: [
        { time: "2:00 PM", activity: "Registration and welcome", icon: "UserCheck" },
        { time: "2:30 PM", activity: "Leadership theory and best practices", icon: "BookOpen" },
        { time: "4:00 PM", activity: "Interactive exercises and role-playing", icon: "Play" },
        { time: "5:30 PM", activity: "Action planning and next steps", icon: "Target" }
      ],
      image: "assets/images/mis/training.jpg"
    }
  };

  const tabs = [
    { id: 'meeting', label: 'General Meeting', icon: 'Users' },
    { id: 'project', label: 'Service Project', icon: 'Heart' },
    { id: 'training', label: 'Training Workshop', icon: 'BookOpen' }
  ];

  const currentActivity = activities[activeTab];

  return (
    <section className="py-20 bg-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center space-y-4 mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
            A Day in the Life of a Leo Member
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Experience the variety and richness of Leo Club activities through typical member experiences across different types of engagements.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-col sm:flex-row justify-center mb-12">
          <div className="bg-card rounded-xl p-2 shadow-lg">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-smooth ${activeTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-md'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                >
                  <Icon name={tab.icon} size={20} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Activity Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Activity Details */}
          <div className="space-y-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name={tabs.find(t => t.id === activeTab)?.icon} size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground">{currentActivity.title}</h3>
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Icon name="Clock" size={16} />
                      <span className="text-sm">{currentActivity.time}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Icon name="MapPin" size={16} />
                      {currentActivity.locationLink ? (
                        <a
                          href={currentActivity.locationLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline"
                        >
                          {currentActivity.location}
                        </a>
                      ) : (
                        <span className="text-sm">{currentActivity.location}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {currentActivity.description}
              </p>
            </div>

            {/* Schedule */}
            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-foreground">Typical Schedule</h4>
              <div className="space-y-3">
                {currentActivity.schedule.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4 p-4 bg-card rounded-lg">
                    <div className="w-10 h-10 bg-surface rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon} size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <span className="text-sm font-semibold text-primary">{item.time}</span>
                      </div>
                      <p className="text-sm text-foreground">{item.activity}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Activity Image */}
          <div className="space-y-6">
            <div className="relative">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-xl">
                <Image
                  src={currentActivity.image}
                  alt={currentActivity.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl"></div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <h4 className="text-lg font-semibold mb-1">{currentActivity.title}</h4>
                <p className="text-sm text-gray-200">{currentActivity.location}</p>
              </div>
            </div>


          </div>
        </div>
      </div>
    </section>
  );
};

export default DayInLifeSection;